import validator from "validator";

import { isErrorResponse } from "../../../api/common/error";
import { createAccount } from "../../../api/user/createAccount";
import { useRestrictedEmailDomains } from "../../../api/utility/useRestrictedEmailDomains";
import { updateSingleToggleService } from "../../../Common/NotificationSettings/NotificationSettingsUtils";
import { FormSaveFn } from "../../../Common/EditProfile/formContext";
import { FORM_VALIDATOR, validateRequiredKeys } from "../../../Common/formUtils";
import { getUtmParamsWithReferrerFallback } from "../../../Common/queryParamsUtils";
import { useRooTranslation } from "../../../Common/Wrappers/useRooTranslation";
import { SET_AUTHORISE_USER } from "../../../constants/Login";
import { UserType } from "../../../constants/UserTypeConstants";
import { formatPhoneNumber } from "../../../helpers/textUtility";
import { initBraze } from "../../../Init/thirdPartyInit";
import { useAppDispatch } from "../../../store/service";
import { Event, EventTracker } from "../../../tracking/service/EventTracker/EventTrackerService";
import { useRedirect } from "../Common/hooks/useRedirect";
import { useRegistrationContext } from "../Common/registrationContext";
import { getIsEntity, saveRegistrationContextToLocalStorage, useIsEntity } from "../Common/service";
import * as Sentry from "@sentry/react";

import { RegistrationSignUpFormType } from "./RegistrationSignUpFormContext";

const isEmailDomainAllowed = (email: string, restrictedEmailDomains: string[]) => {
  if (restrictedEmailDomains.length === 0) {
    return true;
  }

  const emailDomain = email.toLowerCase().split("@")[1];
  if (emailDomain) {
    return !restrictedEmailDomains.includes(emailDomain);
  }
  return true;
};

export const useValidate = () => {
  const { t } = useRooTranslation();
  const { data } = useRestrictedEmailDomains();
  const restrictedEmailDomains = data?.domains || [];
  const { isVet, isTech } = useIsEntity();
  const isContractor = isVet || isTech;

  const validate = (form: RegistrationSignUpFormType) => {
    const requiredKeys: (keyof RegistrationSignUpFormType)[] = ["email", "password", "phone"];
    const errors = validateRequiredKeys({ form, requiredKeys });
    // @ts-expect-error TS2532
    if (!("phone" in form) || form["phone"]?.toString().length < 10) {
      errors["phone"] = "Please enter a valid phone number";
    }
    if (!FORM_VALIDATOR.isPassword(form["password"])) {
      errors["password"] = "Please enter a valid password";
    }
    if (!validator.isEmail(form["email"])) {
      errors["email"] = "Please enter a valid email";
    } else if (isContractor && !isEmailDomainAllowed(form["email"], restrictedEmailDomains)) {
      errors["email"] = t("registration.signUp.form.personalEmailRequired");
    }

    return errors;
  };

  return { validate };
};

const sendTrackingEvent = async ({
  userType,
  entityId,
  context,
}: {
  userType: UserType;
  entityId: number;
  context?: any;
}): Promise<void> => {
  const { isVet, isTech } = getIsEntity(userType);

  if (isVet || isTech) {
    EventTracker.send({
      eventName: isVet ? Event.Name.VET_CLICK_CONTACT : Event.Name.TECH_CLICK_CONTACT,
      eventType: Event.Type.CLICK,
      entityType: isVet ? Event.Entity.VET : Event.Entity.TECH,
      entityId: entityId,
      context: context,
    });
  }
};

export const useSave = () => {
  const dispatch = useAppDispatch();
  const { registrationContext, setRegistrationContext } = useRegistrationContext();
  const { redirectToLocation } = useRedirect();

  const save: FormSaveFn<RegistrationSignUpFormType> = async (form, { setErrors }) => {
    const request = {
      ...getUtmParamsWithReferrerFallback(),
      firstName: registrationContext.firstName,
      lastName: registrationContext.lastName,
      // @ts-expect-error TS2345
      phoneNumber: formatPhoneNumber(form.phone),
      email: form.email,
      password: form.password,
      userTypeId: registrationContext.userType,
      isFromMobile: 0,
      referralCodeId: registrationContext.referralCode,
    };

    // @ts-expect-error TS2345
    const data = await createAccount(request);

    if (isErrorResponse(data)) {
      // @ts-expect-error TS2722
      setErrors((prevErrors) => ({ ...prevErrors, email: data.message }));
      return Promise.reject();
    }

    const entityId =
      data.data.vetId || data.data.techId || data.data.studentId || data.data.hospitalId;

    sendTrackingEvent({
      // @ts-expect-error TS2322
      userType: registrationContext.userType,
      // @ts-expect-error TS2322
      entityId,
    });

    const newRegistrationContext = {
      ...registrationContext,
      ...form,
      userId: data.data.userId,
      entityId,
    };

    // Braze set up since we have the userId now.
    if (data.data.userId) {
      initBraze({ userId: data.data.userId.toString() });
    }

    setRegistrationContext({
      // @ts-expect-error TS2322
      registrationContext: newRegistrationContext,
    });

    // @ts-expect-error TS2322
    saveRegistrationContextToLocalStorage(newRegistrationContext);

    // Update marketing SMS preference if user opted in
    try {
      await updateSingleToggleService({
        userId: data.data.userId,
        notificationCode: 19, // Marketing SMS notification code
        config: {
          smsEnabled: form.isMarketingSMSEnabled || false,
          emailEnabled: true,
          pushEnabled: true,
        },
      });
    } catch (error) {
      // Log error but don't block registration
      Sentry.captureException(error);
    }

    dispatch({ type: SET_AUTHORISE_USER });
    redirectToLocation();
    return Promise.resolve();
  };

  return { save };
};
